import type { StateCreator } from "../store";
import type { AppStore } from "../index";
import { Message, Group, Community } from "@/data/schemas";
import { parseJwtPayload } from "@/core_bridge/module_api";
import { createDirectThreadKey, createGroupThreadKey } from "@/core_bridge/thread_keys";
import { getAnnouncementPermission } from "@/features/community/communityService";
import { stateServices } from "@/state/module_api";

// Thread is essentially a Group in thread context
export type Thread = Group;

export interface DisableInputState {
  status: boolean;
  reason: string | undefined;
}

export interface SelectedThread {
  threadKey: string | undefined;
  title: string | undefined;
  receiverId: string | undefined;
  senderId: string | undefined;
  groupId: string | undefined;
  isLoading: boolean;
  error: string | undefined;
  messages: Message[];
  hasMoreOlderMessages: boolean;
}

export interface ThreadSearch {
  query: string;
  results: Message[];
  isSearching: boolean;
  error: string | undefined;
}

export interface ThreadSelection {
  threadKey?: string;
  receiverId?: string;
  senderId?: string;
  groupId?: string;
  withUserId?: string;
  title?: string;
  disableInput?: DisableInputState | false;
}

// Generic loading state for async operations
export interface LoadingState<T> {
  isLoading: boolean;
  error: string | null;
  items: T[];
}

// Member types
export interface CommunityMember {
  userId: string;
  communityId: string;
  role: string;
  joinedAt: number;
}

export interface GroupMember {
  userId: string;
  groupId: string;
  role: string;
  joinedAt: number;
}

export interface ThreadSlice {
  // State
  selectedThread: SelectedThread;
  threadSearch: ThreadSearch;
  selection: ThreadSelection;
  selectedThreadId: string | undefined;
  messagesByThread: Record<string, Message[]>;
  hasMoreByThread: Record<string, boolean>;
  natsChatMessages: LoadingState<Message>;
  userCommunities: LoadingState<Community>;
  natsChatSelectedCommunity: Community | null;
  natsChatSelectedGroup: Group | null;
  communityGroups: LoadingState<Group>;
  userGroups: LoadingState<Group>;
  communityMembers: LoadingState<CommunityMember>;
  groupMembers: LoadingState<GroupMember>;
  wsConnected: boolean;

  // Actions
  selectThread: (selection?: ThreadSelection) => void;
  setSelectedThread: (thread: Partial<SelectedThread>) => void;
  clearSelectedThread: () => void;
  setThreadMessages: (messages: Message[]) => void;
  addThreadMessage: (message: Message) => void;
  addThreadMessages: (messages: Message[]) => void;
  updateThreadMessage: (messageId: string, updates: Partial<Message>) => void;
  removeThreadMessage: (messageId: string) => void;
  setThreadLoading: (isLoading: boolean) => void;
  setThreadError: (error: string | undefined) => void;
  setHasMoreOlderMessages: (hasMore: boolean) => void;
  setMessages: (threadKey: string, messages: Message[]) => void;
  appendMessage: (threadKey: string, message: Message) => void;
  updateMessage: (
    threadKey: string,
    messageId: string,
    updates: Partial<Message>
  ) => void;
  setHasMore: (threadKey: string, hasMore: boolean) => void;

  // Search actions
  setSearchQuery: (query: string) => void;
  setSearchResults: (results: Message[]) => void;
  setIsSearching: (isSearching: boolean) => void;
  setSearchError: (error: string | undefined) => void;
  clearSearch: () => void;

  setNatsChatMessages: (messages: Message[]) => void;
  addNatsChatMessage: (message: Message) => void;
  setNatsChatMessagesLoading: (isLoading: boolean) => void;
  setNatsChatMessagesError: (error: string | null) => void;

  setUserCommunities: (communities: Community[]) => void;
  addUserCommunity: (community: Community) => void;
  setUserCommunitiesLoading: (isLoading: boolean) => void;
  setUserCommunitiesError: (error: string | null) => void;

  setNatsChatSelectedCommunity: (community: Community | null) => void;
  setNatsChatSelectedGroup: (group: Group | null) => void;

  setCommunityGroups: (groups: Group[]) => void;
  setCommunityGroupsLoading: (isLoading: boolean) => void;
  setCommunityGroupsError: (error: string | null) => void;

  setUserGroups: (groups: Group[]) => void;
  addUserGroup: (group: Group) => void;
  setUserGroupsLoading: (isLoading: boolean) => void;
  setUserGroupsError: (error: string | null) => void;

  setCommunityMembers: (members: CommunityMember[]) => void;
  setCommunityMembersLoading: (isLoading: boolean) => void;
  setCommunityMembersError: (error: string | null) => void;

  setGroupMembers: (members: GroupMember[]) => void;
  setGroupMembersLoading: (isLoading: boolean) => void;
  setGroupMembersError: (error: string | null) => void;

  setWsConnected: (connected: boolean) => void;
  clearThreadRuntimeState: () => void;

  // Computed getters (functions, not hooks)
  getThread: () => Thread | undefined;
  getDisableInput: () => DisableInputState | false;
  isWebviewActiveInThreadView: () => boolean;
  getCommunityJoinedGroups: () => Group[];
  getCommunityAvailableGroups: () => Group[];
}

const initialSelectedThread: SelectedThread = {
  threadKey: undefined,
  title: undefined,
  receiverId: undefined,
  senderId: undefined,
  groupId: undefined,
  isLoading: false,
  error: undefined,
  messages: [],
  hasMoreOlderMessages: false,
};

const initialSearch: ThreadSearch = {
  query: "",
  results: [],
  isSearching: false,
  error: undefined,
};

const createLoadingState = <T>(): LoadingState<T> => ({
  isLoading: false,
  error: null,
  items: [],
});

export const createThreadSlice: StateCreator<AppStore, unknown, unknown, ThreadSlice> = (set, get) => ({
  // Initial state
  selectedThread: { ...initialSelectedThread },
  threadSearch: { ...initialSearch },
  selection: {},
  selectedThreadId: undefined,
  messagesByThread: {},
  hasMoreByThread: {},
  natsChatMessages: createLoadingState<Message>(),
  userCommunities: createLoadingState<Community>(),
  natsChatSelectedCommunity: null,
  natsChatSelectedGroup: null,
  communityGroups: createLoadingState<Group>(),
  userGroups: createLoadingState<Group>(),
  communityMembers: createLoadingState<CommunityMember>(),
  groupMembers: createLoadingState<GroupMember>(),
  wsConnected: false,

  // Actions
  selectThread: (selection = {}) =>
    set((state) => {
      const receiverId = selection.receiverId || selection.withUserId;
      const senderId =
        selection.senderId ||
        (state.apiToken ? parseJwtPayload(state.apiToken)?.user_id : undefined);
      const resolvedThreadId =
        selection.threadKey ||
        (selection.groupId ? createGroupThreadKey(selection.groupId) : undefined) ||
        (senderId && receiverId
          ? createDirectThreadKey(senderId, receiverId)
          : undefined);

      if (!resolvedThreadId && !receiverId && !senderId && !selection.groupId) {
        state.selection = {};
        state.selectedThreadId = undefined;
        state.selectedThread = { ...initialSelectedThread };
        stateServices.sessionState.setActiveThreadId(null);
        return;
      }

      const isGroupThread = resolvedThreadId?.startsWith("group:");
      const groupId =
        selection.groupId || (isGroupThread ? resolvedThreadId.replace("group:", "") : undefined);
      const title =
        selection.title ||
        (senderId && receiverId && senderId === receiverId
          ? "Note to Self"
          : undefined);

      state.selectedThreadId = resolvedThreadId;
      stateServices.sessionState.setActiveThreadId(resolvedThreadId || null);
      state.selection = {
        threadKey: resolvedThreadId,
        receiverId,
        senderId,
        groupId,
        withUserId: selection.withUserId,
        title,
      };

      const threadMessages = resolvedThreadId
        ? state.messagesByThread[resolvedThreadId] || []
        : [];

      state.selectedThread = {
        ...state.selectedThread,
        threadKey: resolvedThreadId,
        receiverId,
        senderId,
        groupId,
        title,
        messages: threadMessages,
        hasMoreOlderMessages: resolvedThreadId
          ? !!state.hasMoreByThread[resolvedThreadId]
          : false,
        isLoading: false,
        error: undefined,
      };

      let disableInput: DisableInputState | false = false;
      const thread = groupId ? state.threadMap[groupId] : undefined;
      if (
        thread &&
        "membershipStatus" in thread &&
        (thread as any).membershipStatus?.status === "exited"
      ) {
        disableInput = {
          status: true,
          reason: "You have exited this group.",
        };
      } else if (thread && thread.isAnnouncementGroup) {
        const myUserId = state.apiToken
          ? parseJwtPayload(state.apiToken)?.user_id
          : undefined;
        const community = thread.communityId
          ? state.communityMap?.[thread.communityId]
          : undefined;
        const canPost = community
          ? getAnnouncementPermission(community as any, myUserId)
          : thread.ownerId === myUserId;
        if (!canPost) {
          disableInput = {
            status: true,
            reason: "Only owner or admin can broadcast.",
          };
        }
      }
      state.selection.disableInput = disableInput;
    }),

  setSelectedThread: (thread) =>
    set((state) => {
      Object.assign(state.selectedThread, thread);
    }),

  clearSelectedThread: () =>
    set((state) => {
      state.selectedThread = { ...initialSelectedThread };
      state.selection = {};
      state.selectedThreadId = undefined;
    }),

  setThreadMessages: (messages) =>
    set((state) => {
      state.selectedThread.messages = messages;
      if (state.selectedThread.threadKey) {
        state.messagesByThread[state.selectedThread.threadKey] = messages;
      }
    }),

  addThreadMessage: (message) =>
    set((state) => {
      state.selectedThread.messages.push(message);
      if (state.selectedThread.threadKey) {
        state.messagesByThread[state.selectedThread.threadKey] =
          state.selectedThread.messages;
      }
    }),

  addThreadMessages: (messages) =>
    set((state) => {
      state.selectedThread.messages.push(...messages);
      if (state.selectedThread.threadKey) {
        state.messagesByThread[state.selectedThread.threadKey] =
          state.selectedThread.messages;
      }
    }),

  updateThreadMessage: (messageId, updates) =>
    set((state) => {
      const index = state.selectedThread.messages.findIndex(
        (m) => m.id === messageId
      );
      if (index !== -1) {
        Object.assign(state.selectedThread.messages[index], updates);
        if (state.selectedThread.threadKey) {
          state.messagesByThread[state.selectedThread.threadKey] =
            state.selectedThread.messages;
        }
      }
    }),

  removeThreadMessage: (messageId) =>
    set((state) => {
      state.selectedThread.messages = state.selectedThread.messages.filter(
        (m) => m.id !== messageId
      );
      if (state.selectedThread.threadKey) {
        state.messagesByThread[state.selectedThread.threadKey] =
          state.selectedThread.messages;
      }
    }),

  setThreadLoading: (isLoading) =>
    set((state) => {
      state.selectedThread.isLoading = isLoading;
    }),

  setThreadError: (error) =>
    set((state) => {
      state.selectedThread.error = error;
    }),

  setHasMoreOlderMessages: (hasMore) =>
    set((state) => {
      state.selectedThread.hasMoreOlderMessages = hasMore;
      if (state.selectedThread.threadKey) {
        state.hasMoreByThread[state.selectedThread.threadKey] = hasMore;
      }
    }),

  setMessages: (threadKey, messages) =>
    set((state) => {
      state.messagesByThread[threadKey] = messages;
      if (state.selectedThreadId === threadKey) {
        state.selectedThread.messages = messages;
      }
    }),

  appendMessage: (threadKey, message) =>
    set((state) => {
      if (!state.messagesByThread[threadKey]) {
        state.messagesByThread[threadKey] = [];
      }
      state.messagesByThread[threadKey].push(message);
      if (state.selectedThreadId === threadKey) {
        state.selectedThread.messages = state.messagesByThread[threadKey];
      }
    }),

  updateMessage: (threadKey, messageId, updates) =>
    set((state) => {
      const messages = state.messagesByThread[threadKey];
      if (!messages) return;
      const index = messages.findIndex((m) => m.id === messageId);
      if (index !== -1) {
        Object.assign(messages[index], updates);
      }
      if (state.selectedThreadId === threadKey) {
        state.selectedThread.messages = messages;
      }
    }),

  setHasMore: (threadKey, hasMore) =>
    set((state) => {
      state.hasMoreByThread[threadKey] = hasMore;
      if (state.selectedThreadId === threadKey) {
        state.selectedThread.hasMoreOlderMessages = hasMore;
      }
    }),

  // Search actions
  setSearchQuery: (query) =>
    set((state) => {
      state.threadSearch.query = query;
    }),

  setSearchResults: (results) =>
    set((state) => {
      state.threadSearch.results = results;
    }),

  setIsSearching: (isSearching) =>
    set((state) => {
      state.threadSearch.isSearching = isSearching;
    }),

  setSearchError: (error) =>
    set((state) => {
      state.threadSearch.error = error;
    }),

  clearSearch: () =>
    set((state) => {
      state.threadSearch = { ...initialSearch };
    }),

  setNatsChatMessages: (messages) =>
    set((state) => {
      state.natsChatMessages.items = messages;
    }),

  addNatsChatMessage: (message) =>
    set((state) => {
      state.natsChatMessages.items.push(message);
    }),

  setNatsChatMessagesLoading: (isLoading) =>
    set((state) => {
      state.natsChatMessages.isLoading = isLoading;
    }),

  setNatsChatMessagesError: (error) =>
    set((state) => {
      state.natsChatMessages.error = error;
    }),

  setUserCommunities: (communities) =>
    set((state) => {
      state.userCommunities.items = communities;
    }),

  addUserCommunity: (community) =>
    set((state) => {
      state.userCommunities.items.push(community);
    }),

  setUserCommunitiesLoading: (isLoading) =>
    set((state) => {
      state.userCommunities.isLoading = isLoading;
    }),

  setUserCommunitiesError: (error) =>
    set((state) => {
      state.userCommunities.error = error;
    }),

  setNatsChatSelectedCommunity: (community) =>
    set((state) => {
      state.natsChatSelectedCommunity = community;
    }),

  setNatsChatSelectedGroup: (group) =>
    set((state) => {
      state.natsChatSelectedGroup = group;
    }),

  setCommunityGroups: (groups) =>
    set((state) => {
      state.communityGroups.items = groups;
    }),

  setCommunityGroupsLoading: (isLoading) =>
    set((state) => {
      state.communityGroups.isLoading = isLoading;
    }),

  setCommunityGroupsError: (error) =>
    set((state) => {
      state.communityGroups.error = error;
    }),

  setUserGroups: (groups) =>
    set((state) => {
      state.userGroups.items = groups;
    }),

  addUserGroup: (group) =>
    set((state) => {
      state.userGroups.items.push(group);
    }),

  setUserGroupsLoading: (isLoading) =>
    set((state) => {
      state.userGroups.isLoading = isLoading;
    }),

  setUserGroupsError: (error) =>
    set((state) => {
      state.userGroups.error = error;
    }),

  setCommunityMembers: (members) =>
    set((state) => {
      state.communityMembers.items = members;
    }),

  setCommunityMembersLoading: (isLoading) =>
    set((state) => {
      state.communityMembers.isLoading = isLoading;
    }),

  setCommunityMembersError: (error) =>
    set((state) => {
      state.communityMembers.error = error;
    }),

  setGroupMembers: (members) =>
    set((state) => {
      state.groupMembers.items = members;
    }),

  setGroupMembersLoading: (isLoading) =>
    set((state) => {
      state.groupMembers.isLoading = isLoading;
    }),

  setGroupMembersError: (error) =>
    set((state) => {
      state.groupMembers.error = error;
    }),

  setWsConnected: (connected) =>
    set((state) => {
      state.wsConnected = connected;
    }),

  clearThreadRuntimeState: () =>
    set((state) => {
      state.selectedThread = { ...initialSelectedThread };
      state.threadSearch = { ...initialSearch };
      state.selection = {};
      state.selectedThreadId = undefined;
      state.messagesByThread = {};
      state.hasMoreByThread = {};
      state.natsChatMessages = createLoadingState<Message>();
      state.userCommunities = createLoadingState<Community>();
      state.natsChatSelectedCommunity = null;
      state.natsChatSelectedGroup = null;
      state.communityGroups = createLoadingState<Group>();
      state.userGroups = createLoadingState<Group>();
      state.communityMembers = createLoadingState<CommunityMember>();
      state.groupMembers = createLoadingState<GroupMember>();
      state.wsConnected = false;
    }),

  // Computed getters
  getThread: () => {
    const state = get();
    const { groupId } = state.selectedThread;
    if (groupId) {
      return state.threadMap[groupId];
    }
    return undefined;
  },

  getDisableInput: () => {
    const state = get();
    const { groupId } = state.selectedThread;
    const thread = groupId ? state.threadMap[groupId] : undefined;

    // Check membership status
    if (
      thread &&
      "membershipStatus" in thread &&
      (thread as any).membershipStatus?.status === "exited"
    ) {
      return {
        status: true,
        reason: "You have exited this group.",
      };
    }

    // Check announcement group permissions
    if (thread && thread.isAnnouncementGroup) {
      // Need to get myUserId from auth state
      const myUserId = state.apiToken
        ? parseJwtPayload(state.apiToken)?.user_id
        : undefined;
      const community = thread.communityId
        ? state.communityMap?.[thread.communityId]
        : undefined;
      const canPost = community
        ? getAnnouncementPermission(community as any, myUserId)
        : thread.ownerId === myUserId;

      if (!canPost) {
        return {
          status: true,
          reason: "Only owner or admin can broadcast.",
        };
      }
    }

    return false;
  },

  isWebviewActiveInThreadView: () => {
    const { selectedThread } = get();
    return !!(
      selectedThread.receiverId ||
      selectedThread.senderId ||
      selectedThread.groupId
    );
  },

  getCommunityJoinedGroups: () => {
    const { userGroups, communityGroups } = get();
    return userGroups.items.filter((group) =>
      communityGroups.items.some((cg) => cg.id === group.id)
    );
  },

  getCommunityAvailableGroups: () => {
    const { userGroups, communityGroups } = get();
    return communityGroups.items.filter(
      (group) => !userGroups.items.some((ug) => ug.id === group.id)
    );
  },
});
